// ─────────────────────────────────────────────────────────────────
// Glass-i — DGU Consumables ISSUE page (Stage DGU-CONSUMABLE-3).
//
// Manual issue of DGU consumables (spacer / desiccant / sealants / corner keys)
// to production. The operator picks a released work order — the system SUGGESTS
// spacer metres from the unit dims (perimeter × spacer-frames, net) — reviews /
// edits the consumable lines, and confirms. Each line DEDUCTS from the SEPARATE
// DGU inventory (hard-blocked against negative stock, idempotent). Issues are
// REVERSIBLE: reverse returns every line's qty. Kept apart from glass stock.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Input, Select, Tag, Typography, Alert, Button, Space, message,
  Modal, Form, InputNumber, Tooltip, Popconfirm, Descriptions } from 'antd';
import { ReloadOutlined, BorderOuterOutlined, PlusOutlined, DeleteOutlined,
  ThunderboltOutlined, UndoOutlined, PrinterOutlined } from '@ant-design/icons';

import ReportViewerModal from '../../../../components/common/ReportViewerModal';
import { dguConsumableIssueApi } from './dguConsumableIssueApi';
import { workOrdersApi } from '../../production/workOrders/workOrdersApi';
import { itemsApi } from '../../masters/items/itemsApi';
import { KIND_OPTIONS, kindLabel, kindColor } from '../../masters/dguConsumables/dguConsumableHelpers';
import { suggestQtyFor, basisFor, fmtQty, num } from './dguIssueHelpers';
import useAuth from '../../../../hooks/useAuth';

const { Title, Text } = Typography;

const CAN_ISSUE = 'erp.inventory.dgu_consumable.issue';
const CAN_REVERSE = 'erp.inventory.dgu_consumable.reverse';
function hasPerm(me, key) {
  const list = (me && me.permissions) || [];
  return list.includes('*') || list.includes(key);
}
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v); if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
}
let LINE_SEQ = 1;
const blankLine = () => ({ key: `L${LINE_SEQ++}`, itemId: undefined, qty: null, kind: null, basis: 'MANUAL' });

// ── New-issue modal: pick a WO → suggest → edit lines → confirm ──
function NewIssueModal({ open, onClose, onDone, items }) {
  const [form] = Form.useForm();
  const [woOptions, setWoOptions] = useState([]);
  const [woLoading, setWoLoading] = useState(false);
  const [workOrder, setWorkOrder] = useState(null);
  const [geometry, setGeometry] = useState(null);
  // Server's per-item suggestion { itemId → { qty, kind, basis } }. Authoritative
  // over the client geometry calc — it already splits spacer metres across multiple
  // spacer products, which suggestQtyFor (a single-item fallback) cannot.
  const [suggestByItem, setSuggestByItem] = useState({});
  const [suggesting, setSuggesting] = useState(false);
  const [lines, setLines] = useState([blankLine()]);
  const [saving, setSaving] = useState(false);
  const woTimer = useRef(null);

  const itemById = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => m.set(it.itemId, it));
    return m;
  }, [items]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setWorkOrder(null); setGeometry(null); setLines([blankLine()]); setWoOptions([]); setSuggestByItem({});
  }, [open, form]);

  const searchWo = useCallback((val) => {
    if (woTimer.current) clearTimeout(woTimer.current);
    woTimer.current = setTimeout(async () => {
      setWoLoading(true);
      try {
        const data = await workOrdersApi.list({ wono: (val || '').trim() || undefined, page: 1, page_size: 20 });
        const rows = (data && data.rows) || [];
        setWoOptions(rows.map((r) => ({ value: r.id, label: `${r.woNo}${r.status ? ' · ' + r.status : ''}` })));
      } catch { setWoOptions([]); }
      finally { setWoLoading(false); }
    }, 300);
  }, []);

  // Load the spacer suggestion + geometry for the chosen WO.
  const runSuggest = useCallback(async (workOrderId) => {
    if (!workOrderId) { setWorkOrder(null); setGeometry(null); return; }
    setSuggesting(true);
    try {
      const data = await dguConsumableIssueApi.suggest(workOrderId);
      setWorkOrder(data.workOrder || null);
      setGeometry(data.geometry || null);
      const matched = (data.suggestions || []).filter((s) => s.itemId && s.qty > 0);
      // Keep the per-item suggestion so a manual re-pick / ⚡ uses the SAME split
      // (esp. spacer metres divided across products), not the whole-WO total.
      const map = {};
      matched.forEach((s) => { map[s.itemId] = { qty: s.qty, kind: s.kind || null, basis: s.basis || 'MANUAL' }; });
      setSuggestByItem(map);
      const suggested = matched
        .map((s) => ({ key: `L${LINE_SEQ++}`, itemId: s.itemId, qty: s.qty, kind: s.kind || null, basis: s.basis || 'MANUAL' }));
      setLines(suggested.length ? suggested.concat([blankLine()]) : [blankLine()]);
      const unmatched = (data.suggestions || []).filter((s) => !s.itemId);
      if (unmatched.length) {
        message.warning(`${unmatched.length} consumable not matched to a stock item — pick it manually.`);
      }
      if (!(data.geometry && data.geometry.spacerMeters > 0)) {
        message.info('This work order has no DGU units with spacers — add lines manually if needed.');
      }
    } catch (e) { message.error(errMsg(e, 'Could not load the work order BOM.')); }
    finally { setSuggesting(false); }
  }, []);

  const setLine = (key, patch) => setLines((p) => p.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((p) => p.concat([blankLine()]));
  const removeLine = (key) => setLines((p) => (p.length <= 1 ? p : p.filter((l) => l.key !== key)));

  // The server's per-item suggestion wins (it splits spacer metres across products);
  // the client geometry calc is only a fallback for an item not in the WO BOM.
  const suggestedQtyForItem = useCallback((itemId) => {
    const server = itemId ? suggestByItem[itemId] : null;
    if (server && num(server.qty) > 0) return num(server.qty);
    const it = itemById.get(itemId);
    return it && geometry ? suggestQtyFor(it, geometry) : 0;
  }, [suggestByItem, itemById, geometry]);

  const onPickItem = (key, itemId) => {
    const it = itemById.get(itemId);
    const kind = it ? it.consumableKind : null;
    const suggested = suggestedQtyForItem(itemId);
    const basis = (suggestByItem[itemId] && suggestByItem[itemId].basis) || basisFor(kind);
    setLine(key, { itemId, kind, basis, qty: suggested > 0 ? suggested : undefined });
  };

  const applySuggestion = (key) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    const suggested = suggestedQtyForItem(line.itemId);
    if (suggested > 0) setLine(key, { qty: suggested });
    else message.info('No suggestion available — enter the quantity.');
  };

  const submit = async () => {
    const clean = lines.filter((l) => l.itemId && num(l.qty) > 0);
    if (!clean.length) { message.error('Add at least one consumable line with a quantity.'); return; }
    const dupe = new Set();
    for (const l of clean) {
      if (dupe.has(l.itemId)) { message.error('The same consumable appears on more than one line — combine them.'); return; }
      dupe.add(l.itemId);
    }
    setSaving(true);
    try {
      const vals = form.getFieldsValue();
      const out = await dguConsumableIssueApi.create({
        workOrderId: workOrder ? workOrder.id : undefined,
        woNo: workOrder ? workOrder.woNo : undefined,
        unitsCount: geometry ? geometry.unitCount : undefined,
        remarks: vals.remarks || undefined,
        lines: clean.map((l) => ({ itemId: l.itemId, qty: num(l.qty), basis: l.basis || 'MANUAL' }))
      });
      message.success(`${out.issueNo} issued — ${out.lines.length} consumable${out.lines.length === 1 ? '' : 's'} deducted.`);
      onClose();
      onDone && onDone();
    } catch (e) { message.error(errMsg(e, 'Issue failed.')); }
    finally { setSaving(false); }
  };

  const lineColumns = [
    { title: 'Consumable', dataIndex: 'itemId', width: 260,
      render: (v, r) => (
        <Select showSearch optionFilterProp="label" size="small" style={{ width: '100%' }} placeholder="Select consumable"
          value={v} onChange={(val) => onPickItem(r.key, val)}
          options={(items || []).map((it) => ({ value: it.itemId, label: `${it.itemName} · ${kindLabel(it.consumableKind)}` }))} />
      ) },
    { title: 'Kind', dataIndex: 'kind', width: 120, render: (v) => (v ? <Tag color={kindColor(v)}>{kindLabel(v)}</Tag> : <Text type="secondary">—</Text>) },
    { title: 'Qty', dataIndex: 'qty', width: 150,
      render: (v, r) => {
        const it = r.itemId ? itemById.get(r.itemId) : null;
        return (
          <Space size={4}>
            <InputNumber size="small" min={0} step={1} style={{ width: 96 }} value={v}
              onChange={(val) => setLine(r.key, { qty: val })} />
            <span style={{ fontSize: 11, color: '#64748b', width: 30 }}>{it ? (it.stockUom || '') : ''}</span>
            {geometry ? (
              <Tooltip title="Use the quantity suggested from this work order's dimensions">
                <Button size="small" type="text" icon={<ThunderboltOutlined />} onClick={() => applySuggestion(r.key)} />
              </Tooltip>
            ) : null}
          </Space>
        );
      } },
    { title: '', dataIndex: 'x', width: 40,
      render: (_, r) => (lines.length > 1 ? <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeLine(r.key)} /> : null) }
  ];

  return (
    <Modal open={open} onCancel={onClose} onOk={submit} okText="Issue &amp; deduct" confirmLoading={saving}
      title="Issue DGU consumables to production" destroyOnClose maskClosable={false} width={720}>
      <Alert type="info" showIcon className="mb-3"
        message="Manual issue"
        description="Pick a work order to auto-suggest spacer metres (perimeter × spacer-frames), review the consumable lines, then confirm. Each line deducts from DGU inventory — a shortfall blocks the whole issue. Issues can be reversed." />
      {/* Layout-only Form — the WO Select is controlled externally (no named field),
          so it must NOT bind the shared `form` instance; only the remarks Form below
          connects to it (AntD allows one Form per useForm instance). */}
      <Form layout="vertical">
        <Form.Item label="Work order (optional — enables suggestions)" className="mb-2">
          <Select showSearch filterOption={false} allowClear loading={woLoading || suggesting}
            placeholder="Search WO no…" onSearch={searchWo} onFocus={() => searchWo('')}
            onChange={(v) => runSuggest(v)} options={woOptions} notFoundContent={woLoading ? 'Searching…' : 'Type a WO no'} />
        </Form.Item>
      </Form>

      {geometry ? (
        <Descriptions size="small" bordered column={4} className="mb-3"
          items={[
            { key: 'u', label: 'DGU units', children: geometry.unitCount },
            { key: 'f', label: 'Spacer frames', children: geometry.spacerFrames },
            { key: 'p', label: 'Perimeter (m)', children: fmtQty(geometry.perimeterMeters) },
            { key: 's', label: 'Spacer (m)', children: fmtQty(geometry.spacerMeters) }
          ]} />
      ) : null}

      <div className="flex items-center justify-between mb-1">
        <Text strong style={{ fontSize: 13 }}>Consumable lines</Text>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addLine}>Add line</Button>
      </div>
      <Table rowKey="key" size="small" columns={lineColumns} dataSource={lines} pagination={false} />

      <Form form={form} layout="vertical" className="mt-3">
        <Form.Item name="remarks" label="Remarks" className="mb-0">
          <Input.TextArea rows={2} maxLength={1000} placeholder="Optional note for this issue" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function DguConsumableIssuePage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState(undefined);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [items, setItems] = useState([]);
  const timer = useRef(null);
  const { me } = useAuth();
  const canIssue = hasPerm(me, CAN_ISSUE);
  const canReverse = hasPerm(me, CAN_REVERSE);

  // Printable A4 issue slip — fetched through axios (Bearer attached) into a
  // sandboxed iframe viewer, mirroring the rework-slip print.
  const [report, setReport] = useState({ open: false, title: '', html: null, loading: false, error: null });
  const lastReportRef = useRef(null);
  const closeReport = useCallback(() => setReport((r) => ({ ...r, open: false })), []);
  const runReport = useCallback(async (cfg) => {
    lastReportRef.current = cfg;
    setReport({ open: true, title: cfg.title, html: null, loading: true, error: null });
    try { const html = await cfg.fetcher(); setReport((r) => ({ ...r, html, loading: false })); }
    catch (e) { setReport((r) => ({ ...r, loading: false, error: errMsg(e, 'Could not render the slip.') })); }
  }, []);
  const openSlip = useCallback((row) => {
    if (!row || !row.id) return;
    runReport({ title: `Issue Slip · ${row.issueNo || ''}`.trim(), fetcher: () => dguConsumableIssueApi.issueSlipHtml(row.id) });
  }, [runReport]);
  const reloadReport = useCallback(() => { if (lastReportRef.current) runReport(lastReportRef.current); }, [runReport]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dguConsumableIssueApi.list({ page, pageSize, status: status || undefined, search: search || undefined });
      setRows((res && res.rows) || []); setTotal((res && res.total) || 0);
    } catch (e) { message.error(errMsg(e, 'Failed to load DGU consumable issues.')); }
    finally { setLoading(false); }
  }, [page, pageSize, status, search]);
  useEffect(() => { load(); }, [load]);

  // Load the consumable master once (for the issue modal's item picker).
  useEffect(() => {
    let alive = true;
    itemsApi.listDguConsumables({ pageSize: 200 })
      .then((res) => { if (alive) setItems((res && res.rows) || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  const onSearch = useCallback((val) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const reverse = useCallback(async (id) => {
    try {
      const out = await dguConsumableIssueApi.reverse(id);
      message.success(`${out.issueNo} reversed — ${out.reversedLines} line${out.reversedLines === 1 ? '' : 's'} returned to stock.`);
      load();
    } catch (e) { message.error(errMsg(e, 'Reversal failed.')); }
  }, [load]);

  const columns = useMemo(() => ([
    { title: 'Issue no', dataIndex: 'issueNo', width: 190, render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: 'Work order', dataIndex: 'woNo', width: 170, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Units', dataIndex: 'unitsCount', width: 80, align: 'right', render: (v) => (v != null ? v : '—') },
    { title: 'Lines', dataIndex: 'linesCount', width: 70, align: 'right' },
    { title: 'Status', dataIndex: 'status', width: 120,
      render: (v) => <Tag color={v === 'REVERSED' ? 'default' : 'green'}>{v}</Tag> },
    { title: 'Date', dataIndex: 'createdAt', width: 130, render: (v) => fmtDate(v) },
    { title: '', key: 'act', width: 180, align: 'right',
      render: (_, r) => (
        <Space size={0}>
          <Button size="small" type="text" icon={<PrinterOutlined />} onClick={() => openSlip(r)}>Slip</Button>
          {r.status === 'ISSUED' && canReverse ? (
            <Popconfirm title="Reverse this issue?" description="Every line's quantity returns to DGU stock." okText="Reverse" okButtonProps={{ danger: true }}
              onConfirm={() => reverse(r.id)}>
              <Button size="small" type="text" danger icon={<UndoOutlined />}>Reverse</Button>
            </Popconfirm>
          ) : null}
        </Space>
      ) }
  ]), [canReverse, reverse, openSlip]);

  return (
    <div className="p-4">
      <div className="mb-3">
        <Title level={4} style={{ margin: 0 }}><BorderOuterOutlined style={{ marginRight: 8 }} />DGU Consumable Issues</Title>
        <Text type="secondary">Issue spacer / desiccant / sealants / corner keys to a DGU work order. Deducts from the separate DGU inventory — reversible.</Text>
      </div>
      <Card size="small" bodyStyle={{ paddingTop: 8 }}>
        <div className="flex items-center justify-between mb-2">
          <Space>
            <Input.Search allowClear placeholder="Search issue / WO no" style={{ width: 240 }} onChange={(e) => onSearch(e.target.value)} />
            <Select allowClear placeholder="All statuses" style={{ width: 160 }} value={status}
              onChange={(v) => { setStatus(v || undefined); setPage(1); }}
              options={[{ value: 'ISSUED', label: 'Issued' }, { value: 'REVERSED', label: 'Reversed' }]} />
          </Space>
          <Space>
            {canIssue ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>New issue</Button> : null}
            <Button icon={<ReloadOutlined />} onClick={load} />
          </Space>
        </div>
        <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} sticky scroll={{ x: 'max-content' }}
          pagination={{ current: page, pageSize, total, showSizeChanger: true, size: 'small', pageSizeOptions: ['25', '50', '100'],
            onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
            showTotal: (t) => `${t} issue${t === 1 ? '' : 's'}` }}
          locale={{ emptyText: loading ? ' ' : 'No DGU consumable issues yet — click “New issue” to issue against a work order.' }} />
      </Card>
      <NewIssueModal open={newOpen} onClose={() => setNewOpen(false)} onDone={load} items={items} />
      <ReportViewerModal
        open={report.open} title={report.title} html={report.html}
        loading={report.loading} error={report.error} width="820px"
        onReload={reloadReport} onClose={closeReport}
      />
    </div>
  );
}
